import { forwardRef, useRef, useState } from 'react'

import { FaCirclePause, FaCirclePlay } from 'react-icons/fa6'

import IconChevronRight from '@/assets/icons/icon-chevron-right.svg'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { ImageWithPlaceholder } from '@/modules/tiktok-research/ui/ImageWithPlaceholder.tsx'

import type { SoundType } from '../../types'

interface Props {
  item: SoundType
  toggleSelect: (soundId: SoundType['id']) => void
  isSelected: boolean
  onClickViewVideos: () => void
}

export const SoundCard = forwardRef<HTMLDivElement, Props>(
  ({ item, toggleSelect, isSelected, onClickViewVideos }, ref) => {
    const audio = useRef<HTMLAudioElement | null>(null)
    const [isAudioPlaying, setIsAudioPlaying] = useState(false)
    const handleCheckboxChange = () => {
      toggleSelect(item.id)
    }

    const handleToggleAudio = () => {
      if (!audio.current) return

      if (audio.current.paused) {
        setIsAudioPlaying(true)
        audio.current.play()
      } else {
        setIsAudioPlaying(false)
        audio.current.pause()
      }
    }

    const handleEnd = () => {
      setIsAudioPlaying(false)
    }

    const AudioIcon = isAudioPlaying ? FaCirclePause : FaCirclePlay

    return (
      <div
        className="bg-card w-full space-y-4 rounded-lg p-4"
        ref={ref}
      >
        <div className="relative overflow-hidden rounded">
          <div className="h-[200px]">
            <ImageWithPlaceholder
              className="h-full w-full object-cover"
              src={item.coverPhoto}
              alt="Sound"
            />
          </div>
          <audio
            onEnded={handleEnd}
            ref={audio}
            itemType="audio/mpeg"
            src={item.audio}
          />

          {item.audio && (
            <AudioIcon
              size={26}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 cursor-pointer text-white"
              onClick={handleToggleAudio}
            />
          )}

          <div className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center gap-2 rounded-full bg-black bg-opacity-30">
            <Checkbox
              checked={isSelected}
              className="border bg-white"
              onCheckedChange={handleCheckboxChange}
            />
          </div>
        </div>
        <div className="max-w-full font-medium">
          <div className="mb-2 overflow-hidden text-ellipsis whitespace-nowrap text-lg leading-6">
            {item.title}
          </div>
          <div className="text-secondary-400 dark:text-secondary-300 mb-6 text-sm">
            {item.author}
          </div>
          <div className="color-secondary-500 bg-primary-50 dark:bg-secondary-600 w-max max-w-full rounded px-3 py-2">
            {item.postCount} Trending Videos
          </div>
        </div>
        <Button
          variant="ghost"
          className="p-0"
          onClick={onClickViewVideos}
        >
          View Videos
          <IconChevronRight className="ml-1" />
        </Button>
      </div>
    )
  },
)

SoundCard.displayName = 'SoundCard'
